import React from 'react';
import styled from 'styled-components';
import { 
  MapPin, 
  Mail, 
  Clock, 
  Facebook, 
  Twitter, 
  Linkedin,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import logo from '../assets/logo1.jpeg';

const FooterContainer = styled.footer`
  background: linear-gradient(180deg, #001a45 0%, #002664 100%);
  color: var(--white);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--accent-gold), var(--secondary-red), var(--accent-gold));
  }
`;

const FooterTop = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 2rem 3rem;
  display: grid;
  grid-template-columns: 1.2fr 0.5fr 1fr 1.2fr;
  gap: 2rem;
  position: relative;
  z-index: 1;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2.5rem;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    padding: 3rem 1.5rem 2rem;
  }
`;

const FooterSection = styled.div`
  h3 {
    font-family: var(--font-secondary);
    font-size: 1.1rem;
    margin-bottom: 1.25rem;
    color: var(--accent-gold);
    font-weight: 700;
    letter-spacing: 0.02em;
  }
`;

// DIV 1: Logo et description
const FooterLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;

  img {
    height: 80px;
    width: auto;
  }
`;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
  
  span {
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.8);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-weight: 600;
  }
  
  strong {
    font-family: var(--font-secondary);
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.3;
    color: var(--white);
  }
`;

const FooterDescription = styled.p`
  font-size: 0.9rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 1.25rem;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const SocialIcon = styled.a`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition);
  color: var(--white);

  &:hover {
    background: var(--accent-gold);
    color: var(--primary-blue-dark);
    transform: translateY(-3px);
  }
`;

// DIV 2: Vide
const EmptyDiv = styled.div`
  @media (max-width: 1024px) {
    display: none;
  }
`;

// DIV 3: Services
const FooterLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  transition: var(--transition);
  font-weight: 500;

  svg {
    opacity: 0;
    transform: translateX(-10px);
    transition: var(--transition);
    color: var(--accent-gold);
  }

  &:hover {
    color: var(--white);
    padding-left: 0.5rem;

    svg {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

// DIV 4: Contact + Newsletter
const ContactItem = styled.a`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.875rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  transition: var(--transition);
  line-height: 1.5;

  svg {
    color: var(--accent-gold);
    flex-shrink: 0;
    margin-top: 2px;
  }

  &:hover {
    color: var(--accent-gold);
  }
`;

const Newsletter = styled.div`
  background: rgba(255, 255, 255, 0.08);
  padding: 1rem;
  border-radius: var(--radius-lg);
  margin-top: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.1);

  h4 {
    font-size: 0.9rem;
    margin-bottom: 0.3rem;
    font-weight: 700;
    color: var(--white);
  }

  p {
    font-size: 0.8rem;
    margin-bottom: 0.75rem;
    color: rgba(255, 255, 255, 0.75);
  }
`;

const NewsletterForm = styled.form`
  display: flex;
  gap: 0.4rem;

  input {
    flex: 1;
    padding: 0.6rem 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: var(--radius-md);
    background: rgba(255, 255, 255, 0.1);
    color: var(--white);
    font-size: 0.85rem;

    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }

    &:focus {
      outline: none;
      border-color: var(--accent-gold);
    }
  }

  button {
    padding: 0.6rem 0.875rem;
    background: var(--accent-gold);
    color: var(--primary-blue-dark);
    border-radius: var(--radius-md);
    font-weight: 700;
    font-size: 0.8rem;
    transition: var(--transition);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(254, 202, 0, 0.3);
    }
  }
`;

const FooterBottom = styled.div`
  background: rgba(0, 0, 0, 0.25);
  padding: 1.25rem 2rem;
  position: relative;
  z-index: 1;
`;

const FooterBottomContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 640px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
`;

const FooterBottomLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  justify-content: center;

  a {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.7);
    transition: var(--transition);
    font-weight: 500;

    &:hover {
      color: var(--accent-gold);
    }
  }
`;

const Footer = () => {
  return (
    (<FooterContainer>
      <FooterTop>
        {/* DIV 1: Logo et description */}
        <FooterSection>
          <FooterLogo>
            <img src={logo} alt="Médiature de la République" />
            <LogoText>
              <span>République du Tchad</span>
              <strong>Médiature de la République</strong>
            </LogoText>
          </FooterLogo>
          
          <FooterDescription>
            Autorité administrative indépendante chargée de la médiation entre 
            l'Administration publique et les citoyens tchadiens.
          </FooterDescription>

          <SocialLinks>
            <SocialIcon href="#" aria-label="Facebook">
              <Facebook size={18} strokeWidth={2} />
            </SocialIcon>
            <SocialIcon href="#" aria-label="Twitter">
              <Twitter size={18} strokeWidth={2} />
            </SocialIcon>
            <SocialIcon href="#" aria-label="LinkedIn">
              <Linkedin size={18} strokeWidth={2} />
            </SocialIcon>
          </SocialLinks>
        </FooterSection>

        {/* DIV 2: Vide */}
        <EmptyDiv />

        {/* DIV 3: Services */}
        <FooterSection>
          <h3>Services</h3>
          <FooterLink to="/services/depot-plainte">
            <ChevronRight size={16} />
                      Déposer une Réclamation
                    </FooterLink>
          <FooterLink to="/services/suivi">
            <ChevronRight size={16} />
            Suivre ma réclamation
          </FooterLink>
          <FooterLink to="/observatoire">
            <ChevronRight size={16} />
            Observatoire
          </FooterLink>
          <FooterLink to="/documents">
            <ChevronRight size={16} />
            Publications
          </FooterLink>
          <FooterLink to="/contact">
            <ChevronRight size={16} />
            FAQ
          </FooterLink>
        </FooterSection>

        {/* DIV 4: Contact + Newsletter */}
        <FooterSection>
          <h3>Contact</h3>
          <ContactItem href="#">
            <MapPin size={16} />
            Avenue Charles de Gaulle, N'Djamena
          </ContactItem>
          <ContactItem href="mailto:contact@mediature.td">
            <Mail size={16} />
            contact@mediature.td
          </ContactItem>
          <ContactItem href="#">
            <Clock size={16} />
            Lun-Ven: 8h00 - 16h00
          </ContactItem>

          {/* Newsletter en dessous de Contact */}
          <Newsletter>
            <h4>Newsletter</h4>
            <p>Recevez nos actualités</p>
            <NewsletterForm>
              <input type="email" placeholder="Votre email" />
              <button type="submit">OK</button>
            </NewsletterForm>
          </Newsletter>
        </FooterSection>
      </FooterTop>
      <FooterBottom>
        <FooterBottomContent>
          <Copyright>
            © {new Date().getFullYear()} Médiature de la République du Tchad. Tous droits réservés.
          </Copyright>
          <FooterBottomLinks>
            <a href="#">Mentions légales</a>
            <a href="#">Politique de confidentialité</a>
            <a href="#">Accessibilité</a>
          </FooterBottomLinks>
        </FooterBottomContent>
      </FooterBottom>
    </FooterContainer>)
  );
};

export default Footer;

